import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { testimonials } from '@/mocks/testimonials';
import { faqItems } from '@/mocks/faq';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于我们 - 精品久久久',
  description: '了解精品久久久的品牌故事、核心价值和团队理念。精品久久久是高品质精选内容长效平台，汇聚经久不衰的优质内容与精品资源。',
  url: 'https://www.jingpin999.com/about',
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqItems.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.answer,
    },
  })),
};

const testimonialSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '精品久久久',
  review: testimonials.map((t) => ({
    '@type': 'Review',
    author: { '@type': 'Person', name: t.name },
    reviewBody: t.content,
    reviewRating: { '@type': 'Rating', ratingValue: t.rating, bestRating: 5 },
  })),
};

const breadcrumbItems = [
  { label: '首页', href: '/' },
  { label: '关于我们' },
];

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(testimonialSchema) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 md:pt-24">
          {/* Header */}
          <section className="relative bg-background-950 overflow-hidden py-16 md:py-24">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=Abstract%20warm%20gradient%20with%20soft%20golden%20and%20cream%20tones%2C%20elegant%20flowing%20textures%2C%20minimalist%20abstract%20art%2C%20premium%20and%20sophisticated%20feel&width=1920&height=500&seq=about-hero-bg&orientation=landscape"
                alt=""
                className="w-full h-full object-cover opacity-20"
              />
            </div>
            <div className="relative z-10 max-w-4xl mx-auto px-4 md:px-6 text-center">
              <h1 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-background-50 mb-5">
                关于精品久久久
              </h1>
              <p className="text-base md:text-lg text-background-300/80 max-w-2xl mx-auto leading-relaxed">
                精品久久久是您值得信赖的高品质精选平台，汇聚经久不衰的优质内容与精品资源，为您提供持久、稳定、高价值的极致体验。
              </p>
            </div>
          </section>

          {/* Brand Story */}
          <section className="py-14 md:py-20">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 items-center">
                <div>
                  <div className="aspect-[4/3] w-full overflow-hidden rounded-xl">
                    <img
                      src="https://readdy.ai/api/search-image?query=Elegant%20minimalist%20workspace%20with%20laptop%20and%20books%20on%20wooden%20desk%2C%20warm%20natural%20light%2C%20cream%20beige%20tones%2C%20peaceful%20editorial%20atmosphere%2C%20sophisticated%20aesthetic&width=800&height=600&seq=about-brand-story&orientation=landscape"
                      alt="精品久久久品牌故事"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                <div>
                  <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-900 mb-5">
                    品牌故事
                  </h2>
                  <div className="space-y-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                    <p>
                      精品久久久诞生于2020年，创始团队的初心很简单——在信息泛滥的时代，为追求品质生活的读者打造一个值得信赖的<strong>精品内容长效平台</strong>。
                    </p>
                    <p>
                      我们深知，在海量信息中筛选真正有价值的内容并非易事。因此，我们组建了一支由资深编辑、行业专家和文化学者组成的团队，以"宁缺毋滥"为原则，坚持严格的<strong>三审三校</strong>流程，确保每一篇内容都经得起时间的考验。
                    </p>
                    <p>
                      从最初的生活美学专栏，到如今涵盖深度阅读、人物风尚、科技前沿、艺术设计、健康养生六大内容板块的综合性平台，<strong>精品久久久资源</strong>始终保持着对品质的执着追求。
                    </p>
                    <p>
                      我们相信，真正的好内容值得被反复品读。在这个追求"快"的时代，<strong>精品久久久</strong>选择"慢"下来，用心打磨每一篇<strong>高品质精品内容</strong>，为读者提供持久、稳定、高价值的<strong>精品持久体验</strong>。
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Values */}
          <section className="py-14 md:py-20 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10 md:mb-14">
                <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-900 mb-3">
                  核心价值
                </h2>
                <p className="text-sm md:text-base text-foreground-500 max-w-lg mx-auto">
                  我们坚守的品质准则，是精品久久久持续发展的根基
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
                {[
                  {
                    icon: 'ri-shield-check-line',
                    title: '品质至上',
                    desc: '每一篇文章都经过严格筛选，坚持"三审三校"流程，确保内容经得起时间考验。我们不追求数量，只专注于品质。',
                  },
                  {
                    icon: 'ri-lightbulb-line',
                    title: '深度原创',
                    desc: '汇聚行业专家与资深作者，产出有深度、有观点、有温度的高品质原创内容，为读者提供独特的阅读体验。',
                  },
                  {
                    icon: 'ri-user-heart-line',
                    title: '读者为本',
                    desc: '始终以读者需求为导向，持续优化内容质量和阅读体验。50,000+忠实读者的信赖，是我们最大的动力。',
                  },
                  {
                    icon: 'ri-bookmark-line',
                    title: '长效价值',
                    desc: '拒绝快餐式内容，专注于具有长期价值的精品文章。好内容永不褪色，每一次重读都有新的收获。',
                  },
                  {
                    icon: 'ri-earth-line',
                    title: '开放包容',
                    desc: '拥抱多元观点与跨学科视角，在开放中保持独立判断，为读者呈现立体、丰富的知识图景。',
                  },
                  {
                    icon: 'ri-rocket-line',
                    title: '持续创新',
                    desc: '不断探索内容呈现的新形式，在坚守品质的同时拥抱变化，让精品内容以更好的方式触达每一位读者。',
                  },
                ].map((item, i) => (
                  <div
                    key={i}
                    className="p-6 md:p-7 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all"
                  >
                    <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-4">
                      <i className={`${item.icon} text-xl`}></i>
                    </div>
                    <h3 className="font-heading font-semibold text-foreground-900 mb-2.5 text-base">
                      {item.title}
                    </h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Stats */}
          <section className="py-14 md:py-20">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
                {[
                  { value: '200+', label: '精选文章' },
                  { value: '6', label: '内容分类' },
                  { value: '50K+', label: '忠实读者' },
                  { value: '99.7%', label: '内容好评率' },
                ].map((stat, i) => (
                  <div key={i} className="text-center">
                    <div className="font-heading font-bold text-3xl md:text-4xl text-primary-600 mb-2">
                      {stat.value}
                    </div>
                    <div className="text-sm text-foreground-500">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section className="py-14 md:py-20 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10 md:mb-14">
                <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-900 mb-3">
                  用户口碑
                </h2>
                <p className="text-sm md:text-base text-foreground-500 max-w-lg mx-auto">
                  来自读者、作者和合作伙伴的真实评价
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {testimonials.map((t, i) => (
                  <div
                    key={i}
                    className="p-5 md:p-6 rounded-xl bg-background-50 border border-background-200/70"
                  >
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: t.rating }).map((_, j) => (
                        <i key={j} className="ri-star-fill text-accent-500 text-sm"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                      &ldquo;{t.content}&rdquo;
                    </p>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-heading font-bold text-sm">
                        {t.name.charAt(0)}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground-800">{t.name}</div>
                        <div className="text-xs text-foreground-500">{t.title}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="py-14 md:py-20">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10 md:mb-14">
                <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-900 mb-3">
                  常见问题
                </h2>
                <p className="text-sm md:text-base text-foreground-500">
                  关于精品久久久，您可能想了解的问题
                </p>
              </div>

              <div className="space-y-3">
                {faqItems.map((item, i) => (
                  <details
                    key={i}
                    className="group rounded-xl bg-background-50 border border-background-200/70 overflow-hidden"
                  >
                    <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
                      <h4 className="font-medium text-foreground-800 text-sm md:text-base pr-4">
                        {item.question}
                      </h4>
                      <div className="w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 group-open:bg-primary-100 group-open:text-primary-600 transition-colors">
                        <i className="ri-add-line group-open:ri-subtract-line text-sm"></i>
                      </div>
                    </summary>
                    <div className="px-5 pb-5">
                      <p className="text-sm text-foreground-600 leading-relaxed">{item.answer}</p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className="py-14 md:py-20 bg-background-100">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10 md:mb-14">
                <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-900 mb-3">
                  联系我们
                </h2>
                <p className="text-sm md:text-base text-foreground-500">
                  如果您有任何问题、建议或合作意向，欢迎与我们取得联系
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 mb-10">
                {[
                  {
                    icon: 'ri-mail-line',
                    title: '电子邮件',
                    content: 'contact@jingpin999.com',
                    desc: '一般咨询与商务合作',
                  },
                  {
                    icon: 'ri-send-plane-line',
                    title: '投稿邮箱',
                    content: 'submit@jingpin999.com',
                    desc: '原创内容投稿与合作',
                  },
                  {
                    icon: 'ri-wechat-line',
                    title: '微信公众号',
                    content: '精品久久久',
                    desc: '搜索关注，获取每日精选',
                  },
                ].map((item, i) => (
                  <div
                    key={i}
                    className="p-5 md:p-6 rounded-xl bg-background-50 border border-background-200/70 text-center"
                  >
                    <div className="w-11 h-11 mx-auto mb-3 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                      <i className={`${item.icon} text-xl`}></i>
                    </div>
                    <h3 className="font-heading font-semibold text-foreground-900 mb-1.5 text-sm">
                      {item.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mb-2">{item.desc}</p>
                    <p className="text-sm font-medium text-primary-600">{item.content}</p>
                  </div>
                ))}
              </div>

              {/* Contact Form */}
              <form
                data-readdy-form="true"
                id="about-contact-form"
                action="https://readdy.ai/api/form/d9rcracnlsngrm4lmaa0"
                method="POST"
                target="_blank"
                className="max-w-xl mx-auto bg-background-50 rounded-xl border border-background-200/70 p-6 md:p-8"
              >
                <h3 className="font-heading font-semibold text-foreground-900 mb-5 text-center text-lg">
                  发送消息
                </h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="about-name" className="block text-sm text-foreground-600 mb-1.5">
                        姓名
                      </label>
                      <input
                        type="text"
                        id="about-name"
                        name="name"
                        required
                        className="w-full px-3.5 py-2.5 rounded-lg border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="about-email" className="block text-sm text-foreground-600 mb-1.5">
                        邮箱
                      </label>
                      <input
                        type="email"
                        id="about-email"
                        name="email"
                        required
                        className="w-full px-3.5 py-2.5 rounded-lg border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="about-subject" className="block text-sm text-foreground-600 mb-1.5">
                      主题
                    </label>
                    <input
                      type="text"
                      id="about-subject"
                      name="subject"
                      required
                      className="w-full px-3.5 py-2.5 rounded-lg border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="消息主题"
                    />
                  </div>
                  <div>
                    <label htmlFor="about-message" className="block text-sm text-foreground-600 mb-1.5">
                      消息内容
                    </label>
                    <textarea
                      id="about-message"
                      name="message"
                      rows={4}
                      required
                      maxLength={500}
                      className="w-full px-3.5 py-2.5 rounded-lg border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                      placeholder="请输入您想说的内容（不超过500字）"
                    ></textarea>
                  </div>
                  <input
                    type="text"
                    name="company_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="hp-field"
                  />
                  <button
                    type="submit"
                    className="w-full py-3 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    发送消息
                  </button>
                </div>
              </form>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}